import { Box, IconButton } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { client as elevioClient } from 'elevio';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { BeamerButton } from 'components/beamerButton';
import Profile from 'components/profile/containers/profile';
import { CommandLauncherTopbarButton } from 'components/topbar/components/commandLauncherButton';
import { EmailTopbarButton } from 'components/topbar/components/email';
import { NotificationTopbarButton } from 'components/topbar/components/notifications';
import { SmsTopbarButton } from 'components/topbar/components/sms';
import { TopbarButton } from 'components/topbar/components/topbarButton';
import TelephonyTopbarButton from 'components/topbar/telephony/containers/telephonyTopbarButton';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery/useBreakpointValue';
import { pubSub } from 'utils/pubSub';

import { TopBarSearchField } from './components/topBarSearchField/topBarSearchField';
import styles from './topBar.module.scss';

export type TopBarProps = {
  onToggleNavigation?: () => void;
};

export const TopBar = ({ onToggleNavigation }: TopBarProps): JSX.Element => {
  const isSmallLayout = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'base' },
  );
  const hasReplyAccess = useHasAccess('conversation.reply');

  const { pathname } = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe('toggleGlobalSearch', ({ isOpen }) => {
      setIsSearchOpen(isOpen ?? !isOpen);
    });

    return () => unsubscribe();
  }, []);

  const { smsCompose, emailChannel, phoneChannel } = useFeatureFlag([
    'sms-compose',
    'email-channel',
    'phone-channel',
  ]);

  const rootClass = classnames(styles.root, {
    [styles.hasSearchOpen]: isSearchOpen,
  });

  return (
    <header className={rootClass}>
      <Box className={styles.menu}>
        <IconButton
          icon="bars"
          variant="secondary"
          onClick={() => onToggleNavigation?.()}
        />
      </Box>

      {!pathname.startsWith('/search') && (
        <Box className={styles.search}>
          <>
            {!isSearchOpen && (
              <IconButton
                icon="search"
                variant="secondary"
                onClick={() => setIsSearchOpen(true)}
                data-testid="top-bar-search-input-button"
              />
            )}
            <TopBarSearchField
              isOpen={isSearchOpen}
              onOpenChange={(isOpen) => setIsSearchOpen(isOpen)}
            />
          </>
        </Box>
      )}

      <Box className={styles.user}>
        <Profile
          isSmall={!!isSmallLayout}
          alignRight={!!isSmallLayout}
          className={styles.profile}
        />
      </Box>

      <Box className={styles.actions}>
        {!isSmallLayout && <CommandLauncherTopbarButton />}
        {hasReplyAccess && smsCompose && <SmsTopbarButton />}
        {hasReplyAccess && emailChannel && (
          <EmailTopbarButton alignCenter={!!isSmallLayout} />
        )}
        {hasReplyAccess && phoneChannel && (
          <TelephonyTopbarButton alignCenter={!!isSmallLayout} />
        )}

        <NotificationTopbarButton alignCenter={!!isSmallLayout} />
        {!isSmallLayout && <BeamerButton />}

        <TopbarButton
          icon="question"
          tooltip="Help center"
          onClick={() => {
            elevioClient.open();
            ampli.topbarSupportOpened();
          }}
        />
      </Box>
    </header>
  );
};
